// consents.ts — CORE-1404
//
// Согласия пользователя на сервере: версия юридических документов, выбор,
// сделанный на экране регистрации до соцвхода, и синхронизация решения по
// cookie для вошедших.

import type { AgeGroup } from '../screens/auth/ageChoiceLogic';

/**
 * Версия набора «Условия использования + Политика конфиденциальности», которые
 * открываются из формы регистрации, — дата последней смены любого из них.
 *
 * CORE-1932 — в набор российского контура вошло согласие на обработку ПДн
 * (`personal-data-consent` 2026-10-22), версия поднята до его даты.
 *
 * CORE-1560 — согласие на ПДн в редакции 30.10 («отмечая это поле», формулировка
 * для 14–17 лет), версия поднята до её даты.
 *
 * CORE-1660 — это общая метка записи согласия, а не доказательство текста:
 * точные редакции (документ, версия, вариант, SHA-256) уходят отдельным списком
 * `documents` из реестра `legal-editions/` (см. `src/legal/editions.ts`).
 *
 * CORE-1567 — Политика 2026-10-30 (без пп. 10.3–10.6 об OpenAI).
 */
export const LEGAL_DOCUMENTS_VERSION = '2026-10-30';

/** Версия текста баннера cookie и окна настроек (выбор по категориям, CORE-1659). */
export const COOKIE_CONSENT_VERSION = '2026-10-19';
/** Версия прежнего баннера: одно решение «принять / отклонить» на все счётчики. */
export const COOKIE_CONSENT_LEGACY_VERSION = '2026-09-14';

/**
 * CORE-1657 — версия текста рекламного согласия (вторая, необязательная галочка
 * на регистрации). Меняется вместе с `legal.marketingConsent` в словаре `auth`.
 *
 * CORE-1560 — 2026-10-30: «Я даю согласие на получение рекламы от АО «Цифровая
 * ИИ Трансформация»» (ответ юриста 30.10 на вопрос 31) вместо «Оставляя свои
 * контактные данные…».
 */
export const MARKETING_CONSENT_VERSION = '2026-10-30';

export type ConsentSource = 'register' | 'social_gate' | 'banner' | 'settings';

export interface PendingSignupConsent {
  ageGroup: Exclude<AgeGroup, 'under14'> | null;
  legalAccepted: boolean;
  /** CORE-1657 — необязательная рекламная галочка. */
  marketingAccepted: boolean;
}

/**
 * Выбор, сделанный на экране регистрации, до нажатия «Войти через Google/Apple».
 * Соцвход создаёт аккаунт без этих полей; экран согласия после входа (ConsentGate)
 * берёт их отсюда, чтобы не спрашивать второй раз то, что человек уже ответил.
 *
 * Только память процесса: вход через Google (GIS) и Apple не перезагружает
 * страницу, а спрашивать повторно после перезагрузки — правильное поведение.
 */
let pendingSignup: PendingSignupConsent | null = null;

export function setPendingSignupConsent(value: PendingSignupConsent | null): void {
  pendingSignup = value;
}

export function takePendingSignupConsent(): PendingSignupConsent | null {
  const value = pendingSignup;
  pendingSignup = null;
  return value;
}
